/**
 * PatientOverviewTab — the patient's Overview as a HUB of linked blocks (patient pages switch,
 * `?x=patient-pages`; prototype commit b8afdd2, design log 14 Oct "Overview as a hub"). Each block shows
 * the headline of what the chart already holds and links to its full page: Visits → Appointments,
 * Needs attention → Flags, Problems & allergies → the chart, Medications, Documents, Record → Personal details.
 *
 * Every number and name is a field of a row an existing hook returns (no new endpoint, nothing invented):
 *   Visits              `usePatientAppointmentsForChart`  (POST /api/Appointment/ForPatient)
 *   Needs attention     `usePatientFlagsChart`            (GET  /api/Patients/GetFlagByPatientId)
 *   Problems & allergies `usePatientConditions('Active')`, `usePatientAllergies`
 *   Medications         `usePatientMedications(id, true)`
 *   Documents           `usePatientDocuments`             (first page)
 *   Progress            `useCarePlans` (the encounter readout's C5 read): goals on target, the server's verdict
 *   Record              the loaded `Patient`
 *   Care model tasks    `useCareModelRuns` (Workflows C4 + the run reads the studio run view makes): physician only,
 *                       first, and only when the patient has an active care-model run (ELLMS, 23 Oct 2026)
 * The prototype's "Next best actions" block has no production data source: left out.
 *
 * The three muted tinted cards (prototype commit 22ad00f, Antonin 15 Oct "keep the cards"): Problems and allergies
 * (amber), Medications (green), Documents (blue). The prototype's Supplements (with Medications) and Wearables (with
 * Documents) have no production data source (no hook, no endpoint): a gap, not drawn, nothing invented.
 */
import { Icon } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { useCarePlans } from '@/encounter/services/use-care-plans';
import { usePatientFlagsChart } from '@/patients/services/use-patient-flags-chart';
import { firstLabels, liveRows } from '@/patients/lib/overview-headlines';
import { OverviewBlock, OverviewHeadline } from './overview-block';
import { PatientSummaryCards } from './patient-summary-cards';
import { VisitsBlock } from './visits-block';
import { CareModelTasksBlock } from './care-model-tasks-block';
import type { Patient } from '@/types/patient-api';

/** Where a block's link goes: a top-level patient tab, optionally a chart section. */
export interface OverviewTarget {
  tab: 'personal-details' | 'chart' | 'appointments' | 'care-plan';
  section?: string;
}

export interface PatientOverviewTabProps {
  patient: Patient | null;
  onOpen: (target: OverviewTarget) => void;
}

export function PatientOverviewTab({ patient, onOpen }: PatientOverviewTabProps) {
  const patientId = patient?.patientId ?? null;
  const flagsQ = usePatientFlagsChart(patientId);
  const plansQ = useCarePlans(patientId);

  if (!patientId) {
    return (
      <div data-slot="patient-overview" data-state="loading">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const flags = liveRows(flagsQ.data);
  const goals = (plansQ.data ?? []).flatMap((p) => p.goals);
  const goalsMet = goals.filter((g) => g.status === 'met').length;

  return (
    <div data-slot="patient-overview">
      <CareModelTasksBlock patientId={patientId} onOpenCarePlan={() => onOpen({ tab: 'care-plan' })} />
      <VisitsBlock patientId={patientId} onOpen={() => onOpen({ tab: 'appointments' })} />

      <OverviewBlock
        title="Needs attention"
        icon={<Icon name="warning" size={16} color="currentColor" />}
        linkLabel="Flags"
        onOpen={() => onOpen({ tab: 'chart', section: 'flags' })}
      >
        <BlockState loading={flagsQ.isLoading} error={flagsQ.isError}>
          <OverviewHeadline
            count={flags.length}
            noun={flags.length === 1 ? 'flag' : 'flags'}
            items={firstLabels(flags.map((f) => f.flagResponse?.display))}
            emptyText="No flags on file"
          />
        </BlockState>
      </OverviewBlock>

      <PatientSummaryCards patientId={patientId} onOpen={(section) => onOpen({ tab: 'chart', section })} />

      <OverviewBlock
        title="Progress"
        icon={<Icon name="activity" size={16} color="currentColor" />}
        linkLabel="Care plan"
        onOpen={() => onOpen({ tab: 'care-plan' })}
      >
        <BlockState loading={plansQ.isLoading} error={plansQ.isError}>
          <OverviewHeadline
            count={goalsMet}
            noun={`of ${goals.length} goals on target`}
            // Goals still to reach, first: the server's verdict, as sent.
            items={firstLabels(goals.filter((g) => g.status === 'not-met').map((g) => g.title))}
            emptyText={goals.length === 0 ? 'No care plan goals' : 'No goals on target yet'}
          />
        </BlockState>
      </OverviewBlock>
    </div>
  );
}

function BlockState({ loading, error, children }: { loading: boolean; error: boolean; children: React.ReactNode }) {
  if (loading) return <Skeleton className="h-10 w-full" />;
  if (error) return <p data-slot="overview-empty">Could not load. Try again later.</p>;
  return <>{children}</>;
}
